/**
 * @fileoverview MainNav Component - Primary navigation menu with overflow handling
 * 
 * Provides the main navigation menu with:
 * - Responsive navigation with overflow menu
 * - Dynamic navigation items from configuration
 * - Active state highlighting
 * - Collapsible "More" menu for additional items
 * - Icon integration for navigation items
 * - Accessibility support
 * - Mobile-friendly design
 * 
 * @component
 * @example
 * <MainNav />
 * 
 * @requires React
 * @requires react-router-dom
 * @requires ./NavItems
 * @requires ./MainNav.css
 * 
 * @author By John Bellew - Infinia.ie - last updated 10/07/2025
 */

import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { navigationItems } from './NavItems';
import './MainNav.css';

/**
 * MainNav Component
 * 
 * Renders the primary navigation menu with responsive overflow handling.
 * 
 * Features:
 * - Dynamic navigation items from configuration
 * - Responsive design with overflow menu
 * - Active state highlighting
 * - Collapsible "More" menu
 * - Icon integration
 * - Accessibility support
 * 
 * @component
 * @returns {JSX.Element} Primary navigation menu
 */
const MainNav = () => {
  const [showOverflow, setShowOverflow] = useState(false);
  
  // Find the index of Daire
  const daireIndex = navigationItems.findIndex(item => item.path === '/Daire');
  
  // Split the items
  const visibleItems = navigationItems.slice(0, daireIndex + 1);
  const overflowItems = navigationItems.slice(daireIndex + 1);

  return (
    <div className="container">
      <nav className="main-nav">
        <div className="nav-container">
          <ul className="nav-list">
          {visibleItems.map((item) => (
            <li className="nav-item" key={item.path}>
              <NavLink 
                to={item.path} 
                className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              >
                {item.icon && <i className={`${item.icon} nav-icon`}></i>}
                {item.label}
              </NavLink>
            </li>
          ))}
          {overflowItems.length > 0 && (
            <li className="more-menu">
              <button 
                className={`more-button${showOverflow ? ' active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOverflow(!showOverflow);
                }}
                aria-label="More menu items"
              >
                More... <i className={`fas fa-chevron-${showOverflow ? 'up' : 'down'}`}></i>
              </button>
              {showOverflow && (
                <ul className="overflow-menu">
                  {overflowItems.map((item) => (
                    <li key={item.path}>
                      <NavLink 
                        to={item.path} 
                        className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                        onClick={() => setShowOverflow(false)}
                      >
                        {item.icon && <i className={`${item.icon} nav-icon`}></i>}
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}
        </ul>
        </div>
      </nav>
    </div>
  );
};

export default MainNav; 